import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mountain, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DISCLAIMER } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Algo deu errado. Tente novamente.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [mode, setMode] = useState("login"); // "login" | "register"
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);

    const handleGoogle = () => {
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
            const payload = mode === "register" ? form : { email: form.email, password: form.password };
            const { data } = await api.post(endpoint, payload);
            setUser(data);
            toast.success(mode === "register" ? "Conta criada com sucesso!" : "Bem-vindo de volta!");
            const next = new URLSearchParams(window.location.search).get("next");
            navigate(next || "/dashboard");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha na autenticação.");
        } finally {
            setLoading(false);
        }
    };

    const isRegister = mode === "register";

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between bg-darkblue text-white p-12 relative overflow-hidden">
                <div className="absolute inset-0 opacity-20 topo-texture" />
                <Link to="/" className="relative flex items-center gap-2 font-heading font-black text-xl" data-testid="login-logo">
                    <span className="inline-flex items-center justify-center px-2 h-8 rounded bg-mineral text-white text-sm font-black">GM² ANM</span>
                    GEOMINE MANAGER
                </Link>
                <div className="relative">
                    <h2 className="font-heading text-4xl font-black leading-tight">
                        Inteligência e gestão de<br />processos minerários.
                    </h2>
                    <p className="mt-4 text-white/70 max-w-md">
                        Controle sua carteira mineral, acompanhe prazos, consulte legislação com fontes oficiais e conte com uma IA especializada.
                    </p>
                </div>
                <p className="relative text-xs text-white/50 max-w-md">{DISCLAIMER}</p>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <Link to="/" className="lg:hidden flex items-center gap-2 font-heading font-black text-xl text-petroleum mb-8" data-testid="login-logo-mobile">
                        <Mountain className="w-7 h-7 text-mineral" /> GEOMINE MANAGER
                    </Link>

                    <div className="flex rounded-md bg-slate-100 p-1 mb-6" data-testid="auth-mode-switch">
                        <button onClick={() => setMode("login")} data-testid="tab-login"
                            className={`flex-1 py-2 text-sm font-semibold rounded-sm transition-colors ${!isRegister ? "bg-white text-petroleum shadow-sm" : "text-slate-500"}`}>Entrar</button>
                        <button onClick={() => setMode("register")} data-testid="tab-register"
                            className={`flex-1 py-2 text-sm font-semibold rounded-sm transition-colors ${isRegister ? "bg-white text-petroleum shadow-sm" : "text-slate-500"}`}>Criar conta</button>
                    </div>

                    <h1 className="font-heading text-3xl font-black text-slate-900">{isRegister ? "Criar sua conta" : "Entrar na plataforma"}</h1>
                    <p className="text-slate-500 mt-2">{isRegister ? "Cadastre-se com e-mail e senha ou use sua conta Google." : "Acesse com e-mail e senha ou com sua conta Google."}</p>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        {isRegister && (
                            <div>
                                <label className="text-sm font-semibold text-slate-700">Nome completo</label>
                                <input type="text" required value={form.name} onChange={set("name")} data-testid="auth-name-input"
                                    className="mt-1 w-full rounded-md border-2 border-slate-200 focus:border-petroleum outline-none px-4 py-2.5 transition-colors" placeholder="Seu nome" />
                            </div>
                        )}
                        <div>
                            <label className="text-sm font-semibold text-slate-700">E-mail</label>
                            <input type="email" required value={form.email} onChange={set("email")} data-testid="auth-email-input"
                                className="mt-1 w-full rounded-md border-2 border-slate-200 focus:border-petroleum outline-none px-4 py-2.5 transition-colors" placeholder="voce@exemplo.com" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-slate-700">Senha</label>
                            <input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="auth-password-input"
                                className="mt-1 w-full rounded-md border-2 border-slate-200 focus:border-petroleum outline-none px-4 py-2.5 transition-colors" placeholder="Mínimo 6 caracteres" />
                        </div>
                        <button type="submit" disabled={loading} data-testid="auth-submit-button"
                            className="w-full flex items-center justify-center gap-2 bg-petroleum hover:bg-petroleumHover text-white rounded-md px-6 py-3 font-semibold transition-colors disabled:opacity-60">
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                            {isRegister ? "Criar conta" : "Entrar"}
                        </button>
                        {!isRegister && (
                            <div className="text-right">
                                <Link to="/esqueci-senha" data-testid="forgot-password-link" className="text-sm font-semibold text-petroleum hover:underline">Esqueci minha senha</Link>
                            </div>
                        )}
                    </form>

                    <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
                        <div className="h-px flex-1 bg-slate-200" /> OU <div className="h-px flex-1 bg-slate-200" />
                    </div>

                    <button onClick={handleGoogle} data-testid="google-login-button"
                        className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-petroleum rounded-md px-6 py-3 font-semibold text-slate-700 transition-colors">
                        <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5" />
                        Continuar com Google
                    </button>

                    <div className="mt-6 flex items-start gap-2 text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-4">
                        <ShieldCheck className="w-5 h-5 text-petroleum shrink-0 mt-0.5" />
                        <span>Novos usuários iniciam com <strong>14 dias de teste</strong>. Depois, o acesso completo requer a assinatura Geomine Manager Profissional.</span>
                    </div>

                    <p className="mt-6 text-xs text-slate-400 leading-relaxed">{DISCLAIMER}</p>
                </div>
            </div>
        </div>
    );
}
